import React from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, AreaChart, Area, ScatterChart, Scatter } from 'recharts';
import { useGuineaContextStats } from '../../hooks/useGuineaContextStats';
import LoadingSpinner from '../common/LoadingSpinner';
import StatsCard from '../common/StatsCard';
import { formatCurrency, formatNumber } from '../../utils/formatters';

interface Props {
  dateRange: { start_date?: string; end_date?: string };
}

const COLORS = ['#f59e0b', '#10b981', '#ef4444', '#3b82f6', '#8b5cf6'];

export default function GuineaContextTab({ dateRange }: Props) {
  const { data: stats, isLoading, error } = useGuineaContextStats(dateRange);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-500">Erreur lors du chargement des données</div>;
  if (!stats) return null;

  // Données par région de Guinée
  const regionData = stats.regional_distribution.map(region => ({
    region: region.region_name,
    patients: region.patient_count,
    revenue: region.total_revenue,
    distance_avg: region.avg_distance_km
  }));

  // Données des modes de paiement locaux
  const paymentMethodsGuinea = stats.guinea_payment_methods.map(method => ({
    name: method.method_name,
    value: method.amount,
    usage_rate: method.usage_percentage
  }));

  return (
    <div className="space-y-6">
      {/* KPIs Contexte Guinéen */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Patients Ruraux"
          value={`${stats.rural_patients_percentage.toFixed(1)}%`}
          icon="🏘️"
          color="green"
        />
        <StatsCard
          title="Distance Moy. (km)"
          value={formatNumber(stats.avg_travel_distance)}
          icon="🛣️"
          color="blue"
        />
        <StatsCard
          title="Orange/MTN Money"
          value={`${stats.mobile_money_usage.toFixed(1)}%`}
          icon="📱"
          color="orange"
        />
        <StatsCard
          title="Patients CNSS"
          value={formatNumber(stats.cnss_patients)}
          icon="🏥"
          color="purple"
        />
      </div>

      {/* Analyses principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribution régionale */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Distribution par Région de Guinée</h3>
          <ComposedChart width={500} height={350} data={regionData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="region" angle={-45} textAnchor="end" height={100} />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip formatter={(value, name) => [
              name === 'revenue' ? formatCurrency(Number(value)) : 
              name === 'distance_avg' ? `${value} km` : formatNumber(Number(value)),
              name === 'patients' ? 'Patients' : 
              name === 'revenue' ? 'Revenus' : 'Distance Moy.'
            ]} />
            <Legend />
            <Bar yAxisId="left" dataKey="patients" fill="#8884d8" name="Patients" />
            <Line yAxisId="right" type="monotone" dataKey="distance_avg" stroke="#ff7300" strokeWidth={2} name="Distance Moy." />
          </ComposedChart>
        </div>

        {/* Modes de paiement guinéens */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Modes de Paiement en Guinée</h3>
          <PieChart width={500} height={350}>
            <Pie
              data={paymentMethodsGuinea}
              cx={250}
              cy={175}
              labelLine={false}
              label={({ name, usage_rate }) => `${name} ${usage_rate.toFixed(1)}%`}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
            >
              {paymentMethodsGuinea.map((entry, index: number) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [formatCurrency(Number(value)), 'Montant']} />
          </PieChart>
        </div>
      </div>

      {/* Analyse socio-économique */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Impact Socio-Économique par Mois</h3>
        <AreaChart width={1000} height={400} data={stats.socioeconomic_impact}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <Tooltip formatter={(value, name) => [
            name === 'employment_impact' ? formatNumber(Number(value)) :
            name === 'community_health_score' ? `${value}/10` : formatCurrency(Number(value)),
            name === 'local_economy' ? 'Impact Économique' :
            name === 'employment_impact' ? 'Emplois Créés' : 'Score Santé Communautaire'
          ]} />
          <Legend />
          <Area type="monotone" dataKey="local_economy" stackId="1" stroke="#8884d8" fill="#8884d8" name="Économie Locale" />
          <Area type="monotone" dataKey="employment_impact" stackId="2" stroke="#82ca9d" fill="#82ca9d" name="Impact Emploi" />
          <Area type="monotone" dataKey="community_health_score" stackId="3" stroke="#ffc658" fill="#ffc658" name="Santé Communautaire" />
        </AreaChart>
      </div>

      {/* Accessibilité géographique */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Accessibilité vs Fréquentation</h3>
          <ScatterChart width={500} height={350} data={stats.accessibility_analysis}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="distance_km" name="Distance (km)" />
            <YAxis dataKey="visit_frequency" name="Fréquence visites" />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} 
              formatter={(value, name) => [
                name === 'distance_km' ? `${value} km` : `${value} visites/an`,
                name === 'distance_km' ? 'Distance' : 'Fréquence'
              ]} />
            <Scatter dataKey="visit_frequency" fill="#8884d8" />
          </ScatterChart>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Indicateurs Développement</h3>
          <div className="space-y-4">
            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="font-medium text-gray-900">Emplois Locaux Créés</h4>
              <p className="text-2xl font-bold text-green-600">{formatNumber(stats.jobs_created)}</p>
              <p className="text-sm text-gray-500">Direct et indirect</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-medium text-gray-900">Formation Personnel</h4>
              <p className="text-2xl font-bold text-blue-600">{formatNumber(stats.staff_trained)}</p>
              <p className="text-sm text-gray-500">Assistants formés</p>
            </div>
            <div className="border-l-4 border-yellow-500 pl-4">
              <h4 className="font-medium text-gray-900">Partenaires Locaux</h4>
              <p className="text-2xl font-bold text-yellow-600">{formatNumber(stats.local_partnerships)}</p>
              <p className="text-sm text-gray-500">Laboratoires, fournisseurs</p>
            </div>
            <div className="border-l-4 border-red-500 pl-4">
              <h4 className="font-medium text-gray-900">Impact Communautaire</h4>
              <p className="text-2xl font-bold text-red-600">{stats.community_impact_score.toFixed(1)}/10</p>
              <p className="text-sm text-gray-500">Score d'impact social</p>
            </div>
          </div>
        </div>
      </div>

      {/* Défis et opportunités */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">🇬🇳 Défis et Opportunités - Contexte Guinéen</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Défis */}
          <div>
            <h4 className="font-medium text-red-700 mb-3">⚠️ Défis Identifiés</h4>
            <div className="space-y-3">
              <div className="bg-red-50 p-3 rounded-lg">
                <h5 className="font-medium text-red-800">Accessibilité Géographique</h5>
                <p className="text-sm text-red-600">
                  {stats.rural_patients_percentage.toFixed(1)}% de patients ruraux - Distance moyenne {stats.avg_travel_distance} km
                </p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg">
                <h5 className="font-medium text-orange-800">Contraintes Financières</h5>
                <p className="text-sm text-orange-600">
                  {(100 - stats.insurance_coverage_rate).toFixed(1)}% sans couverture santé
                </p>
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg">
                <h5 className="font-medium text-yellow-800">Infrastructure</h5>
                <p className="text-sm text-yellow-600">
                  Besoin de centres satellites dans {stats.underserved_regions} régions
                </p>
              </div>
            </div>
          </div>

          {/* Opportunités */}
          <div>
            <h4 className="font-medium text-green-700 mb-3">🚀 Opportunités</h4>
            <div className="space-y-3">
              <div className="bg-green-50 p-3 rounded-lg">
                <h5 className="font-medium text-green-800">Mobile Money</h5>
                <p className="text-sm text-green-600">
                  {stats.mobile_money_usage.toFixed(1)}% utilisent Orange/MTN Money - Potentiel de croissance
                </p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <h5 className="font-medium text-blue-800">Partenariats Publics</h5>
                <p className="text-sm text-blue-600">
                  Collaboration avec CNSS et ministère de la Santé
                </p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg">
                <h5 className="font-medium text-purple-800">Formation Locale</h5>
                <p className="text-sm text-purple-600">
                  Développer l'expertise locale - {stats.training_potential} candidats potentiels
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}